import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ConversationSessionEntity } from './conversation-session.entity';

/**
 * Stores LLM-generated report definitions — SQL queries, layout metadata, and execution state.
 *
 * PD-16920: the Saved AI Reports feature was removed (the assistant's save tool, the pages that
 * listed and ran these reports, and the API behind them), pending a redesign. These reports run
 * stored SQL against the whole tenant database, so there was no way to limit what a viewer sees to
 * the data their own permissions allow. Nothing reads or writes this table now. The rows are kept,
 * and the entity stays registered so database sync still knows the table, for the redesign that
 * scopes report data to the viewer's own permissions.
 */
@Entity('_ai_report')
export class AiReportEntity extends BaseEntity {
	@Column({ name: 'title', type: 'varchar', length: 500, nullable: false })
	public title!: string;

	@Column({ name: 'description', type: 'text', nullable: true })
	public description?: string;

	/**
	 * Stores the full report definition as JSON.
	 *
	 * Format: `{ "queries": { "key1": "SELECT ...", ... }, "layout": [ { type, queryKey, ... }, ... ], "filters"?: [...] }`
	 *
	 * `queries` is a named map of SQL SELECT queries. Layout blocks reference queries by key.
	 * `layout` is an ordered array of typed blocks (table, chart, metric, container, svg) that
	 * define rendering. `filters` maps `:paramName` placeholders in the SQL to user inputs.
	 */
	@Column({ name: 'query', type: 'longtext', nullable: false })
	public query!: string;

	/**
	 * Previously stored column display config. Now unused for new reports
	 * (layout is stored inside the `query` column's definition).
	 * Kept for schema stability — set to '[]' on new reports.
	 */
	@Column({ name: 'column_config', type: 'longtext', nullable: false })
	public columnConfig!: string;

	/**
	 * Previously stored chart function body. Now unused for new reports
	 * (chartFn is stored inside layout blocks in the `query` column's definition).
	 * Kept for schema stability — set to null on new reports.
	 */
	@Column({ name: 'chart_config', type: 'longtext', nullable: true })
	public chartConfig?: string;

	@Column({
		name: 'status',
		type: 'varchar',
		length: 50,
		nullable: false,
		default: 'active',
	})
	public status!: string;

	@Column({ name: 'last_executed_date', type: 'datetime', nullable: true })
	public lastExecutedDate?: Date;

	/** Last execution error message (null if last execution was successful) */
	@Column({ name: 'last_error', type: 'text', nullable: true })
	public lastError?: string;

	@Column({
		name: 'fk_conversation_session__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	@Index('idx_ai_report_session')
	public fkConversationSessionId?: number;

	@ManyToOne(() => ConversationSessionEntity, { onDelete: 'SET NULL' })
	@JoinColumn({ name: 'fk_conversation_session__id' })
	public session?: ConversationSessionEntity;
}
